import i18next from 'i18next'
import { AsyncState, loadingAsyncState } from 'widgets-for-react'
import { TypeCastingValidator, canSafelyCast } from '../../../utils/safe-type-casting'
import {
  NewUserStatus,
  PromoCodeData,
  PromoCodeResult,
  SchoolsData,
} from './studentSubscriptionState'

async function getSchools(): Promise<any> {
  const headers = new Headers()
  headers.append('content-type', 'application/json')
  const res = await fetch('/api/v1/student-subscription/schools', {
    method: 'get',
    credentials: 'include',
    headers: headers,
  })
  if (!res.ok) throw Error(res.statusText)
  return await res.json()
}

const schoolsDataValidator: TypeCastingValidator<SchoolsData> = {
  kind: 'array',
  elementValidator: {
    kind: 'object',
    fieldsValidator: {
      Id: {
        kind: 'number',
      },
      Title: {
        kind: 'string',
      },
    },
  },
}

export const loadSchools = loadingAsyncState(() =>
  getSchools().then((raw) => {
    const maybeSchools = canSafelyCast<SchoolsData>(schoolsDataValidator, raw)
    return maybeSchools.kind == 'r'
      ? { kind: 'result', value: maybeSchools.v, status: 200 }
      : { kind: 'failed', status: 404 }
  })
)

async function getPromoCode(code: string, schoolId: number): Promise<any> {
  const headers = new Headers()
  headers.append('content-type', 'application/json')
  const res = await fetch(
    `/api/v1/student-subscription/promo-code?code=${code}&schoolId=${schoolId}`,
    {
      method: 'get',
      credentials: 'include',
      headers: headers,
    }
  )
  if ([404, 401, 403].includes(res.status)) {
    return {
      kind: 'failed',
      status: res.status,
    }
  }
  if (!res.ok) throw Error(res.statusText)
  return {
    kind: 'result',
    value: await res.json(),
    status: 200,
  }
}

const promoCodeDataValidator: TypeCastingValidator<PromoCodeData> = {
  kind: 'array',
  elementValidator: {
    kind: 'object',
    fieldsValidator: {
      EndDate: {
        kind: 'string',
      },
    },
  },
}

export const loadPromoCode = (
  code: string,
  schoolId: number
): AsyncState<PromoCodeResult> =>
  loadingAsyncState(() =>
    getPromoCode(code, schoolId)
      .then((raw) => {
        if (raw.kind == 'result') {
          const maybePromoCode = canSafelyCast<PromoCodeData>(
            promoCodeDataValidator,
            raw.value
          )
          return maybePromoCode.kind == 'r'
            ? {
                kind: 'found' as const,
                data: maybePromoCode.v,
              }
            : {
                kind: 'invalid code' as const,
              }
        }
        return raw.status == 404
          ? {
              kind: 'invalid code' as const,
            }
          : raw.status == 403
          ? {
              kind: 'too many attempts' as const,
            }
          : {
              kind: 'error' as const,
            }
      })
      .then((res) => ({
        kind: 'result',
        value: res,
        status: 200,
      }))
  )

async function getEmailKnown(email: string): Promise<any> {
  const headers = new Headers()
  headers.append('content-type', 'application/json')
  const res = await fetch(`/api/v1/student-subscription/email-known/${email}`, {
    method: 'get',
    credentials: 'include',
    headers: headers,
  })
  if (!res.ok) throw Error(res.statusText)
  return await res.json()
}

const emailExistsValidator: TypeCastingValidator<boolean> = {
  kind: 'boolean',
}

export const loadEmailKnown = (email: string) =>
  loadingAsyncState(() =>
    getEmailKnown(email).then((raw) => {
      const maybeEmailExists = canSafelyCast<boolean>(emailExistsValidator, raw)
      return maybeEmailExists.kind == 'r'
        ? {
            kind: 'result',
            value: maybeEmailExists.v,
            status: 200,
          }
        : {
            kind: 'failed',
            status: 404,
          }
    })
  )
type ExistingUserData = {
  Name: string
  Surname: string
  ExpectedGraduationYear: number
  SchoolId: number
}

export async function subscribeExistingUser(
  code: string,
  existingUserData: ExistingUserData
): Promise<any> {
  const headers = new Headers()
  headers.append('content-type', 'application/json')
  const res = await fetch(
    `/api/v1/student-subscription/subscribe-existing-user?code=${code}`,
    {
      method: 'post',
      credentials: 'include',
      headers: headers,
      body: JSON.stringify(existingUserData),
    }
  )
  if (!res.ok) throw Error(res.statusText)
  return
}

export const loadSubscribeExistingUser = (
  code: string,
  existingUserData: ExistingUserData
) =>
  loadingAsyncState(() =>
    subscribeExistingUser(code, existingUserData).then(() => ({
      kind: 'result',
      value: {},
      status: 200,
    }))
  )

type NewUserData = {
  Email: string
  Password: string
  Name: string
  Surname: string
  MarketingEmails: boolean
  ExpectedGraduationYear: number
  SchoolId: number
}

async function subscribeNewUser(
  code: string,
  newUserData: NewUserData
): Promise<any> {
  const headers = new Headers()
  headers.append('content-type', 'application/json')

  const res = await fetch(
    `/api/v1/student-subscription/subscribe-new-user?code=${code}&originLanguage=${i18next.language}`,
    {
      method: 'post',
      headers: headers,
      credentials: 'include',
      body: JSON.stringify(newUserData),
    }
  )
  if (!res.ok) throw Error(res.statusText)
  return await res.json()
}

const subscribeNewUserValidator: TypeCastingValidator<NewUserStatus> = {
  kind: 'object',
  fieldsValidator: {
    status: { kind: 'string' },
    session: { kind: 'string' },
    value: {
      kind: 'object',
      fieldsValidator: {
        id: { kind: 'string' },
        email: { kind: 'string' },
        username: { kind: 'string' },
      },
    },
    role: { kind: 'string' },
  },
}

export const loadSubscribeNewUser = (code: string, newUserData: NewUserData) =>
  loadingAsyncState(() =>
    subscribeNewUser(code, newUserData).then((raw) => {
      const maybeSubscribeNewUser = canSafelyCast<NewUserStatus>(
        subscribeNewUserValidator,
        {
          ...raw,
          session: raw.session || '',
          value: raw.value || { id: '', email: '', username: '' },
          role: raw.role || '',
        }
      )
      return maybeSubscribeNewUser.kind == 'r'
        ? {
            kind: 'result',
            value: maybeSubscribeNewUser.v,
            status: 200,
          }
        : {
            kind: 'failed',
            status: 404,
          }
    })
  )
